import React, { useEffect, useMemo, useState } from "react";
import { Download, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { apiClient } from "@/lib/auth";
import { inputCls } from "./FormRenderer";

const show = (v) => (v === true ? "Yes" : v === false ? "No" : v ?? "");

function toCsv(form, rows) {
  const cols = form.fields.filter((f) => !f.archived);
  const esc = (s) => `"${String(s ?? "").replace(/"/g, '""')}"`;
  const head = ["Entered at", "Entered by", ...cols.map((c) => c.label)].map(esc).join(",");
  const body = rows.map((r) => [r.created_at, r.created_by?.name, ...cols.map((c) => show(r.values[c.key]))].map(esc).join(","));
  return [head, ...body].join("\n");
}

// Searchable log of one custom form's entries — used in the popup and on /admin/records/:formId.
export default function RecordsTable({ formId, compact = false, refreshKey = 0, onChanged }) {
  const [data, setData] = useState({ form: null, records: [] });
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const load = () => {
    const p = new URLSearchParams({ ...(q && { q }), ...(from && { date_from: from }), ...(to && { date_to: to }) });
    apiClient.get(`/admin/ops/forms/${formId}/records?${p}`).then(({ data: d }) => setData(d)).catch(() => toast.error("Could not load records"));
  };
  useEffect(load, [formId, q, from, to, refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const cols = useMemo(() => (data.form?.fields || []).filter((f) => !f.archived).slice(0, compact ? 3 : 50), [data.form, compact]);
  const remove = async (id) => {
    if (!window.confirm("Delete this entry?")) return;
    await apiClient.delete(`/admin/ops/records/${id}`);
    toast.success("Entry deleted"); load(); onChanged?.();
  };
  const exportCsv = () => {
    const blob = new Blob([toCsv(data.form, data.records)], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${data.form.name.replace(/\W+/g, "-").toLowerCase()}-records.csv`;
    a.click();
  };

  return (
    <div data-testid="ops-records" className="space-y-3">
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[140px]">
          <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-slate-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" data-testid="ops-records-search" className={`${inputCls} pl-8`} />
        </div>
        {!compact && <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="ops-records-from" className={`${inputCls} w-auto`} />}
        {!compact && <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="ops-records-to" className={`${inputCls} w-auto`} />}
        <button type="button" onClick={exportCsv} disabled={!data.records.length} data-testid="ops-records-export"
          className="inline-flex items-center gap-1 px-3 py-2 rounded-md border border-slate-700 text-slate-300 hover:text-brand-cyan hover:border-brand-cyan/60 font-mono text-[0.6rem] tracking-[0.14em] uppercase transition disabled:opacity-40">
          <Download className="w-3 h-3" /> CSV
        </button>
      </div>
      <div className="overflow-x-auto rounded-md border border-slate-800">
        <table className="w-full text-sm">
          <thead className="bg-navy-950 text-slate-400 font-mono text-[0.6rem] uppercase tracking-[0.12em]">
            <tr>
              <th className="text-left px-3 py-2">When</th>
              {cols.map((c) => <th key={c.key} className="text-left px-3 py-2">{c.label}</th>)}
              {!compact && <th className="text-left px-3 py-2">By</th>}
              <th />
            </tr>
          </thead>
          <tbody>
            {data.records.map((r) => (
              <tr key={r.id} data-testid={`ops-record-row-${r.id}`} className="border-t border-slate-800 text-slate-200">
                <td className="px-3 py-2 whitespace-nowrap text-slate-400 text-xs">{new Date(r.created_at).toLocaleString()}</td>
                {cols.map((c) => <td key={c.key} className="px-3 py-2">{String(show(r.values[c.key]))}</td>)}
                {!compact && <td className="px-3 py-2 text-slate-400 text-xs">{r.created_by?.name}</td>}
                <td className="px-2 py-2 text-right">
                  <button type="button" onClick={() => remove(r.id)} data-testid={`ops-record-delete-${r.id}`} aria-label="Delete entry"
                    className="p-1.5 rounded text-slate-500 hover:text-rose-300 hover:bg-rose-500/10 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!data.records.length && <div data-testid="ops-records-empty" className="p-6 text-center text-sm text-slate-500">No entries yet.</div>}
      </div>
    </div>
  );
}
